import AiIcon from "../../Core/AiIcon/AiIcon";
import React from "react";
import { AlertCircle, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { AiConversationTurn } from "../../../models/AiAssistant";
import AiDataTable from "./AiDataTable";
import AiMarkdown from "./AiMarkdown";
import AiToolTrace from "./AiToolTrace";
import { stripRedundantTables } from "./stripRedundantTables";

interface AiMessageProps {
	turn: AiConversationTurn;
	/** Where the turn's tables fetch their rows from. Without it a table shows what it was given. */
	conversationId?: string;
	className?: string;
}

/**
 * One turn in the thread. User turns are right-aligned bubbles; assistant
 * turns are plain markdown with the tool trace and any approval decisions
 * that led to them underneath.
 */
const AiMessage: React.FC<AiMessageProps> = ({ turn, conversationId, className }) => {
	if (turn.role === "user") {
		return (
			<div className={cn("flex justify-end", className)}>
				<div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-neutral-800 px-3.5 py-2 text-sm text-white">
					{turn.text}
				</div>
			</div>
		);
	}

	// Rows the tools returned are rendered by AiDataTable; a markdown copy in the text is noise.
	const text = stripRedundantTables(turn.text, turn.datasets).trim();
	const failed = !!turn.error;

	return (
		<div className={cn("flex gap-2.5", className)}>
			<div
				className={cn(
					"mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
					failed ? "bg-danger-muted text-danger" : "bg-brand/15 text-brand"
				)}
			>
				{failed ? <AlertCircle className="h-3.5 w-3.5" /> : <AiIcon className="h-3.5 w-3.5" />}
			</div>
			<div className="min-w-0 flex-1 space-y-1.5">
				{!!turn.approvals?.length && (
					<ul className="space-y-0.5 text-xs text-muted-foreground">
						{turn.approvals.map((approval) => (
							<li key={approval.approvalId} className="flex items-start gap-1">
								{approval.approved ? (
									<Check className="mt-0.5 h-3 w-3 shrink-0 text-success" />
								) : (
									<X className="mt-0.5 h-3 w-3 shrink-0 text-danger" />
								)}
								<span className={cn(!approval.approved && "line-through")}>{approval.summary}</span>
							</li>
						))}
					</ul>
				)}
				{failed ? (
					<div
						className="rounded-md border border-danger/40 bg-danger-muted px-3 py-2 text-sm text-foreground"
						title={turn.error}
					>
						{text || "Något gick fel när svaret togs fram."}
					</div>
				) : text ? (
					<AiMarkdown>{text}</AiMarkdown>
				) : (
					<p className="text-sm italic text-muted-foreground">(inget svar)</p>
				)}
				{turn.datasets?.map((dataset) => (
					<AiDataTable key={dataset.id} dataset={dataset} conversationId={conversationId} />
				))}
				{!!turn.trace?.length && <AiToolTrace trace={turn.trace} usage={turn.usage} timing={turn.timing} />}
			</div>
		</div>
	);
};

export default AiMessage;
